import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  DocumentChartBarIcon as DocumentChartBarIconSolid,
  UserCircleIcon as UserCircleIconSolid,
} from '@heroicons/react/24/solid';

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  activeIcon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: HomeIcon,
    activeIcon: HomeIconSolid,
  },
  {
    name: 'Applications',
    path: '/applications',
    icon: DocumentTextIcon,
    activeIcon: DocumentTextIconSolid,
  },
  {
    name: 'Analytics',
    path: '/analytics',
    icon: ChartBarIcon,
    activeIcon: ChartBarIconSolid,
  },
  {
    name: 'Reports',
    path: '/reports',
    icon: DocumentChartBarIcon,
    activeIcon: DocumentChartBarIconSolid,
  },
  {
    name: 'Profile',
    path: '/profile',
    icon: UserCircleIcon,
    activeIcon: UserCircleIconSolid,
  },
];

export const BottomNavigation: React.FC = () => {
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-area-bottom">
      <div className="grid grid-cols-5 h-16">
        {navItems.map((item) => {
          const active = isActive(item.path);
          const Icon = active ? item.activeIcon : item.icon;

          return (
            <Link
              key={item.path}
              to={item.path}
              className="relative flex flex-col items-center justify-center space-y-1 transition-colors"
            >
              {/* Active indicator */}
              {active && (
                <motion.div
                  layoutId="bottomNavActiveIndicator"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-indigo-600 dark:bg-indigo-500 rounded-full"
                  initial={false}
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30,
                  }}
                />
              )}

              {/* Icon */}
              <Icon
                className={`w-6 h-6 transition-colors ${
                  active
                    ? 'text-indigo-600 dark:text-indigo-500'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              />

              {/* Label */}
              <span
                className={`text-xs font-medium transition-colors ${
                  active
                    ? 'text-indigo-600 dark:text-indigo-500'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {item.name}
              </span>

              {/* Ripple effect on tap */}
              <motion.div
                className="absolute inset-0 bg-indigo-600 dark:bg-indigo-500 rounded-lg opacity-0"
                whileTap={{ opacity: 0.1 }}
                transition={{ duration: 0.2 }}
              />
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
